/* The megamenu's behavior, the website's on the store: a panel opens on click,
   one at a time, and closes on a click elsewhere, on Escape, or when focus
   leaves the bar. In the drawer the same buttons run an accordion and the
   theme's drawer handles Escape. */
class AbzMegamenu extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.mode;
    this.triggers = Array.from(this.querySelectorAll('.abz-mm__trigger'));
    this.onTrigger = (e) => this.toggle(e.currentTarget);
    this.onDocClick = (e) => { if (!this.contains(e.target)) this.closeAll(); };
    this.onKeydown = (e) => {
      if (e.key !== 'Escape') return;
      const open = this.openTrigger();
      if (open) { this.closeAll(); open.focus(); }
    };
    this.onFocusOut = (e) => { if (e.relatedTarget && !this.contains(e.relatedTarget)) this.closeAll(); };
    this.onResize = () => this.closeAll();

    this.triggers.forEach((t) => t.addEventListener('click', this.onTrigger));
    if (this.mode === 'inline') {
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('click', this.onDocClick);
      window.addEventListener('resize', this.onResize, { passive: true });
    }
  }

  disconnectedCallback() {
    this.triggers.forEach((t) => t.removeEventListener('click', this.onTrigger));
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('click', this.onDocClick);
    window.removeEventListener('resize', this.onResize);
  }

  panelFor(trigger) {
    return document.getElementById(trigger.getAttribute('aria-controls'));
  }

  openTrigger() {
    return this.triggers.find((t) => t.getAttribute('aria-expanded') === 'true');
  }

  toggle(trigger) {
    const wasOpen = trigger.getAttribute('aria-expanded') === 'true';
    this.closeAll();
    if (!wasOpen) this.open(trigger);
  }

  open(trigger) {
    const panel = this.panelFor(trigger);
    if (!panel) return;
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    trigger.closest('.abz-mm__item').classList.add('is-open');
    if (this.mode === 'inline') this.place(trigger, panel);
  }

  closeAll() {
    this.triggers.forEach((t) => {
      if (t.getAttribute('aria-expanded') !== 'true') return;
      t.setAttribute('aria-expanded', 'false');
      const panel = this.panelFor(t);
      if (panel) panel.hidden = true;
      t.closest('.abz-mm__item').classList.remove('is-open');
    });
  }

  /* Start the panel at the header's bottom edge and end it at the bar's right
     edge, kept inside the viewport with a 16px gutter. The website placed each
     panel by a hand-tuned offset and let the widest one run off screen. */
  place(trigger, panel) {
    const item = trigger.closest('.abz-mm__item');
    const itemRect = item.getBoundingClientRect();
    const barRect = this.querySelector('.abz-mm__list').getBoundingClientRect();
    const header = this.closest('.wt-header') || this.closest('header');
    if (header) {
      const gap = Math.round(header.getBoundingClientRect().bottom - itemRect.bottom);
      if (gap > 0) panel.style.setProperty('--abz-mm-panel-gap', gap + 'px');
    }
    panel.style.setProperty('--abz-mm-panel-left', '0px');
    const width = panel.offsetWidth;
    const viewport = document.documentElement.clientWidth;
    let left = barRect.right - width - itemRect.left;
    const minLeft = 16 - itemRect.left;
    const maxLeft = viewport - 16 - width - itemRect.left;
    if (left > maxLeft) left = maxLeft;
    if (left < minLeft) left = minLeft;
    panel.style.setProperty('--abz-mm-panel-left', Math.round(left) + 'px');
  }
}

customElements.define('abz-megamenu', AbzMegamenu);
